import type {SpriteImage, Sprite3DMaterial} from './spriteTypes'
import {DEFAULT_3D_SPRITE_MATERIAL} from './spriteTypes'
import {DEFAULT_SPRITE_IMAGE_URL} from '../constants/defaultAssets'

/** Surface a custom decal can be placed on: the flat front face or any side edge. */
export type CustomDecalPlacement = 'front' | 'left' | 'right' | 'top' | 'bottom'

/**
 * A user-supplied image glued to the device surface. Rendered as a flat textured
 * plane (like a 3D sprite) sitting just proud of the front face or a side edge —
 * NOT a CSG cut, so it never touches the geometry worker. Defaults to the Ecranify
 * logo until the user uploads their own image.
 */
export interface CustomDecal extends SpriteImage {
  id: string
  placement: CustomDecalPlacement
  /** In-plane offset along the surface's horizontal axis (mm, from device centre).
   *  front: X | left/right: Z (depth) | top/bottom: X */
  offsetU: number
  /** In-plane offset along the surface's vertical axis (mm).
   *  front: Y | left/right: Y | top/bottom: Z (depth) */
  offsetV: number
  /** Longest side of the plane in mm (the short side is derived from imageAspect). */
  size: number
  /** Rotation about the surface normal, degrees. */
  rotation: number
  /** When true renders unlit (MeshBasicMaterial, no shadows); else PBR + shadows. */
  unlit: boolean
  /** PBR material — used only when unlit is false. */
  material: Sprite3DMaterial
}

export const generateCustomDecalId = (): string => `customdecal-${crypto.randomUUID()}`

/** Create a new custom decal on the given surface, showing the Ecranify logo. */
export const createCustomDecal = (
  placement: CustomDecalPlacement,
  id: string = generateCustomDecalId(),
): CustomDecal => ({
  id,
  imageKey: null,
  imageUrl: DEFAULT_SPRITE_IMAGE_URL,
  imageAspect: 1,
  placement,
  offsetU: 0,
  offsetV: 0,
  size: 20,
  rotation: 0,
  unlit: false,
  material: {...DEFAULT_3D_SPRITE_MATERIAL},
})

/**
 * Plane dimensions (mm) for a decal — longest side equals `size`, short side
 * derived from the image aspect. Mirrors the 3D-sprite sizing convention.
 * Returns [width, height] where width maps to the surface U axis, height to V.
 */
export const customDecalPlaneSize = (decal: CustomDecal): [number, number] => {
  const aspect = decal.imageAspect > 0 ? decal.imageAspect : 1
  return aspect >= 1 ? [decal.size, decal.size / aspect] : [decal.size * aspect, decal.size]
}

/** Smallest allowed decal `size` (mm). */
export const MIN_CUSTOM_DECAL_SIZE = 1

/**
 * Largest `size` (longest side, mm) that still fits the decal's plane within the
 * available surface extents — `su` along the U axis, `sv` along the V axis.
 * Keeps both plane dimensions inside the surface given the image aspect.
 */
export const maxCustomDecalSize = (imageAspect: number, su: number, sv: number): number => {
  const aspect = imageAspect > 0 ? imageAspect : 1
  // size maps to the longest side: aspect>=1 → width=size, height=size/aspect; else height=size, width=size*aspect
  const cap = aspect >= 1 ? Math.min(su, sv * aspect) : Math.min(su / aspect, sv)
  return Math.max(MIN_CUSTOM_DECAL_SIZE, cap)
}
